import type { IOptionProps } from "@fluidattacks/design";
import {
  Button,
  Container,
  Icon,
  ListItemsWrapper,
  useModal,
} from "@fluidattacks/design";
import isUndefined from "lodash/isUndefined";
import { Fragment, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { RemoveCredentials } from "./remove-credentials";
import type { IActionButtonsProps } from "./types";

import { useClickOutside } from "hooks/use-click-outside";

const ActionButtons = ({
  onEdit,
  onRemove,
  selectedCredentials,
  selected,
  permissions,
}: IActionButtonsProps): JSX.Element => {
  const { t } = useTranslation();
  const prefix = "organization.tabs.credentials.actionButtons.";
  const modalRef = useModal("remove-credential-modal");
  const additionalRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);

  const handleDropdown = useCallback((): void => {
    setOpen((previousState): boolean => !previousState);
  }, []);

  const handleRemoveModal = useCallback((): void => {
    modalRef.open();
  }, [modalRef]);

  const options = [
    ...(permissions.canUpdate
      ? [
          {
            disabled: selectedCredentials?.type === "OAUTH",
            icon: "pen",
            label: t(`${prefix}editButton.text`),
            onClick: onEdit,
          } as IOptionProps,
        ]
      : []),
    ...(permissions.canRemove
      ? [
          {
            icon: "trash-alt",
            label: t(`${prefix}removeButton.text`),
            onClick: handleRemoveModal,
          } as IOptionProps,
        ]
      : []),
  ];

  useClickOutside(
    additionalRef.current,
    (): void => {
      setOpen(false);
    },
    true,
  );

  return (
    <Fragment>
      <Container
        alignItems={"center"}
        display={"flex"}
        gap={1}
        position={"relative"}
        ref={additionalRef}
      >
        <Button
          disabled={isUndefined(selectedCredentials) || selected > 1}
          icon={"ellipsis-vertical"}
          id={"manage-credentials"}
          onClick={handleDropdown}
          variant={"ghost"}
        >
          {"Manage credentials"}
        </Button>
        <Container
          display={open ? "block" : "none"}
          maxWidth={"100px"}
          mt={0.25}
          position={"absolute"}
          right={"100px"}
          top={"35px"}
          zIndex={2}
        >
          <ListItemsWrapper
            aria-labelledby={"manage-credentials"}
            role={"menu"}
            tabIndex={-1}
          >
            {options.map((option): JSX.Element => {
              return (
                <Button
                  disabled={option.disabled}
                  id={option.label}
                  justify={"flex-start"}
                  key={option.label}
                  onClick={option.onClick}
                  variant={"ghost"}
                  width={"100%"}
                >
                  <Container pl={0.5}>
                    <Icon
                      icon={option.icon}
                      iconSize={"lg"}
                      iconType={"fa-light"}
                      mr={0.25}
                    />
                    {option.label}
                  </Container>
                </Button>
              );
            })}
          </ListItemsWrapper>
        </Container>
      </Container>
      <RemoveCredentials
        modalRef={modalRef}
        onRemove={onRemove}
        selectedCredentials={selectedCredentials}
      />
    </Fragment>
  );
};

export { ActionButtons };
